import { DOCUMENT } from '@angular/common';
import { Directive, HostListener, Inject, inject, input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
    CasinoCoreNpmParams,
    CasinoCoreNpmParamsService,
    CasinoLobbyService,
    CasinoManager,
    CasinoStylesService,
    ConfigProviderService,
    QuickInfoModalService,
} from '@casinocore/platform/core';
import { DeviceService } from '@frontend/vanilla/core';

import { LibMockService } from '../../services/lib-mock.service';
import { GameModel } from '../models/gametile.models';
import { FeatureConfig, QuickInfoConfig } from '../models/modular-config.models';
import { SubcategoryModel } from '../models/modular-lmt-category.models';
import { GlobalMessage } from '../models/modular-lobby-messages.models';

@Directive({
    selector: '[csMoreInfo]',
    standalone: true,
})
export class MoreInfoDirective {
    game = input.required<GameModel | undefined>();
    categoryType = input.required<SubcategoryModel>();
    lobbyType = input.required<string>();
    stickers = input<{ key: string; value: string }[]>([]);

    private readonly casinoLobbyService = inject(CasinoLobbyService);
    private readonly casinoManager = inject(CasinoManager);
    private readonly deviceService = inject(DeviceService);
    private readonly route = inject(ActivatedRoute);
    private readonly quickInfoModalService = inject(QuickInfoModalService);
    private readonly libMockService = inject(LibMockService);
    private readonly casinoNpmParamsService = inject(CasinoCoreNpmParamsService);
    private readonly configProviderService = inject(ConfigProviderService);
    private readonly casinoStyles = inject(CasinoStylesService);

    constructor(@Inject(DOCUMENT) private document: Document) {}

    @HostListener('click', ['$event'])
    clickHandler(event: Event) {
        event.stopImmediatePropagation();
        const features: FeatureConfig = this.configProviderService.provideFeaturesConfig();
        const quickInfoConfig: QuickInfoConfig = this.configProviderService.provideQuickInfoConfig();
        const globalMessages: GlobalMessage = this.configProviderService.provideGlobalConfig()?.messages;
        const isNewQuickInfoEnabled: boolean = this.casinoManager.IsFeatureEnabledforLobbytypeWithInvokerProduct(
            this.lobbyType(),
            features.enableNewQuickInfoLobbyType,
        );
        const quickInfoScrollY: number = window.pageYOffset;
        const isTouch: boolean = this.deviceService.isTouch;
        const casinoQuickInfoElement = this.document.getElementsByTagName('html')[0];
        const requiredModalData = {
            game: this.game(),
            gameTitle: this.game()?.name,
            src: this.getQuickInfoSrc(),
            useFavouriteGames: this.casinoManager.IsFeatureEnabledforLobbytype(this.game()?.lobbyType ?? '', features.favouriteGamesLobbyType),
            gameProvider: quickInfoConfig?.quickInfo?.gameProviderHeader,
            provider: this.game()?.provider,
            cta: quickInfoConfig?.quickInfo?.cta || globalMessages?.ButtonPlayNow,
            isTouch,
            gamePosition: this.game()?.gameIndex,
            gameLaunchedFrom: 'Top10Games',
            categoryType: this.categoryType()?.subcategoryid,
            isLaunchedFromUrl: false,
            stickers: this.stickers(),
            gridPosition: 1,
            routeParams: this.route.snapshot.params,
            style: globalMessages?.quickInfoStyles,
            product: '',
            lobbyType: this.game()?.lobbyType,
            gameHomeUrl: window.location.href,
            moreInfoText: 'moreInfoEvent',
            icon: this.game()?.icon,
            quickInfoScrollY,
        };

        if (isNewQuickInfoEnabled) {
            this.appendQuickInfoQueryParameterToUrl();
            this.quickInfoModalService.setQuickInfoModalData(requiredModalData);
            return;
        }
        const dialogRef = isTouch
            ? this.libMockService.getQuickInfoService().openGameQuickInfoDialog(requiredModalData)
            : this.libMockService.getQuickInfoService().openQuickInfoDialog(requiredModalData);
        casinoQuickInfoElement.classList.add('casino-quickinfo-open');
        dialogRef.afterClosed().subscribe(() => {
            if (quickInfoScrollY) {
                window.scrollTo(0, quickInfoScrollY);
            }
            isTouch ? this.hideQuickInfo() : this.handleQuickInfoonESC();
        });
    }

    appendQuickInfoQueryParameterToUrl(): void {
        const casinoNpmParams: CasinoCoreNpmParams = this.casinoNpmParamsService.getcasinoCoreNpmParams();
        if (casinoNpmParams?.isDll) {
            const windowUrl: string = window.location.href;
            let pushStateUrl: string;
            let queryStrings: string = '';
            if (this.urlHasQueryStrings(windowUrl)) {
                pushStateUrl = windowUrl.substr(0, windowUrl.indexOf('?'));
                queryStrings = windowUrl.substr(windowUrl.indexOf('?'));
            } else {
                pushStateUrl = windowUrl;
            }
            if (this.isUrlEndsWithSlash(pushStateUrl)) {
                pushStateUrl = pushStateUrl + 'game-info/' + this.game()?.gameId;
            } else {
                pushStateUrl = pushStateUrl + '/game-info/' + this.game()?.gameId;
            }
            pushStateUrl = pushStateUrl + queryStrings;
            window.history.pushState(location.href, '', pushStateUrl);
        }
    }

    isUrlEndsWithSlash(url: string): boolean {
        if (url.lastIndexOf('/') + 1 === url.length) {
            return true;
        }
        return false;
    }

    urlHasQueryStrings(url: string): boolean {
        if (url && url.indexOf('?') > -1) {
            return true;
        }
        return false;
    }

    handleQuickInfoonESC(): void {
        const casinoQuickInfoElement = this.document.getElementsByTagName('html')[0];
        casinoQuickInfoElement.classList.remove('casino-quickinfo-open');
        this.removeQuickInfoQueryParameterFromUrl();
    }
    removeQuickInfoQueryParameterFromUrl(): void {
        const casinoQuickInfoElement = this.document.getElementsByTagName('html')[0];
        const casinoNpmParams: CasinoCoreNpmParams = this.casinoNpmParamsService.getcasinoCoreNpmParams();
        if (casinoNpmParams?.isDll) {
            const param = 'game-info';
            if (location.href.indexOf(param) > -1 && !casinoQuickInfoElement.classList.contains('casino-quickinfo-open')) {
                window.history.pushState(location.href, '', location.href.substr(0, location.href.indexOf(param) - 1));
            }
        }
    }

    hideQuickInfo(): void {
        this.libMockService.getQuickInfoService().closeGameQuickInfoDialog();
    }

    getQuickInfoSrc() {
        let mobileQuickInfoSrc: string = '';
        const feature: FeatureConfig = this.configProviderService.provideFeaturesConfig();
        const newQuickInfoStyle = this.casinoManager.IsFeatureEnabledforLobbytype(this.game()?.lobbyType ?? '', feature.newQuickInfoStyle);
        const useQuickInfo = this.casinoManager.IsFeatureEnabledforLobbytype(this.game()?.lobbyType ?? '', feature.enableQuickInfoLobbyType);
        if (this.game()?.icon?.src) {
            if (newQuickInfoStyle || useQuickInfo) {
                mobileQuickInfoSrc = this.casinoLobbyService.getImagePath(this.game()?.gameId ?? '', 'square');
            }
        }
        if (feature.enableGameIconTheming) {
            mobileQuickInfoSrc = this.casinoStyles.appendTheme(mobileQuickInfoSrc, this.game()?.gameId ?? '');
        }
        return mobileQuickInfoSrc;
    }
}
